import {
  Datagrid,
  ReferenceField,
  TextField,
  FunctionField,
  useRecordContext,
  SimpleForm,
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  AutocompleteInput,
  Button,
  useNotify,
  useRefresh,
  useSimpleFormIteratorItem,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { PreviewExamProtocolButton } from "../../Show/";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import { settings } from "app";
import SecuredComponent from "server/security/SecuredComponent";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import dataProvider from "server/dataProvider";
import { candidateRequests } from "api/candidateApi";
import { useNotifyError } from "utils/useNotifyError";

const IndexField = () => {
  const { index } = useSimpleFormIteratorItem();
  return (
    <TextField
      readOnly
      source=""
      defaultValue={`Приоритет ${index + 1} `}
      sx={{ textAlign: "left" }}
    />
  );
};

const ExamRoomsShow = ({ rooms, exam, campaignIsStarted, hasRoomsWithCandidates }) => {
  const currentRecord = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const notifyError = useNotifyError();

  const [addMode, setAddMode] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const isEditMode = addMode || deleteMode || reorderMode;

  const [selectedExam, setSelectedExam] = useState(null);
  const [initialRooms, setInitialRooms] = useState([]);

  const canDeleteRoom = campaignIsStarted && !hasRoomsWithCandidates;
  const canReorderRooms = campaignIsStarted && !hasRoomsWithCandidates;

  const examRooms = rooms?.length ? rooms : currentRecord?.rooms;

  if (!examRooms) return null;

  const addRoomToExam = (campaignExamId, roomId) =>
    dataProvider.customRequest(candidateRequests.campaignExamRoom(campaignExamId, roomId), null, "POST");

  const deleteRoomToExam = (campaignExamId, roomId) =>
    dataProvider.customRequest(candidateRequests.campaignExamRoom(campaignExamId, roomId), null, "DELETE");

  const reorderRooms = (campaignExamId, reorderedRooms) =>
    dataProvider.customRequest(
      candidateRequests.updateCampaignExamRoomsPriority(campaignExamId),
      { rooms: reorderedRooms },
      "PUT",
    );

  const handleSubmit = async formData => {
    if (!selectedExam) return;

    const campaignExamId = selectedExam.campaignExamId;
    const submittedRooms = formData.rooms ?? [];
    const submittedRoomIds = submittedRooms.map(r => r.roomId).filter(Boolean);
    const initialRoomIds = initialRooms.map(r => r.roomId);
    try {
      // ADD MODE
      if (addMode) {
        const roomsToAdd = submittedRoomIds.filter(id => !initialRoomIds.includes(id));
        await Promise.all(roomsToAdd.map(roomId => addRoomToExam(campaignExamId, roomId)));
      }
      // DELETE MODE
      if (deleteMode) {
        const roomsToDelete = initialRoomIds.filter(id => !submittedRoomIds.includes(id));
        await Promise.all(roomsToDelete.map(roomId => deleteRoomToExam(campaignExamId, roomId)));
      }
      // REORDER MODE
      if (reorderMode) {
        const reorderedRooms = submittedRooms.map((room, idx) => ({
          roomId: room.roomId,
          priority: idx + 1,
        }));
        await reorderRooms(campaignExamId, reorderedRooms);
      }

      notify("Промените са записани успешно", { type: "success" });
      setAddMode(false);
      setDeleteMode(false);
      setReorderMode(false);
      refresh();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Accordion sx={{ width: "100%" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#4E2683" }} />}>
        <Typography
          color={"#4E2683"}
          sx={{ fontWeight: "bold" }}
        >
          Зали за изпита:
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {!isEditMode && (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            data={examRooms}
            sx={settings.style.detailsDatagrid}
          >
            <ReferenceField
              source="roomId"
              label="Зала"
              reference={administrationRequests.getRoomsSelect()}
            >
              <FunctionField
                label="Зала"
                render={rec => `${rec.roomName} - ${rec.buildingName} (${rec.capacityExam})`}
              />
            </ReferenceField>
            <TextField
              source="priority"
              label="Приоритет"
            />
            <TextField
              source="candidatesCount"
              label="Брой записани"
            />
            <TextField
              source="capacity"
              label="Брой места"
            />
            <FunctionField
              label=""
              render={room => (
                <PreviewExamProtocolButton
                  exam={exam}
                  slotId={room.slotId}
                />
              )}
            />
          </Datagrid>
        )}
        {campaignIsStarted && !isEditMode && (
          <SecuredComponent>
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Button
                startIcon={<AddCircleIcon />}
                variant="contained"
                label="Добави"
                sx={{ mt: 1 }}
                size="medium"
                onClick={() => {
                  setSelectedExam(exam);
                  setInitialRooms(examRooms);
                  setAddMode(true);
                }}
              />
              <Button
                startIcon={<RemoveCircleIcon />}
                variant="contained"
                label="Изтрий"
                disabled={!canDeleteRoom}
                sx={{ mt: 1 }}
                size="medium"
                onClick={() => {
                  setSelectedExam(exam);
                  setInitialRooms(examRooms);
                  setDeleteMode(true);
                }}
              />
              <Button
                startIcon={<ChangeCircleIcon />}
                variant="contained"
                label="Размени приоритети"
                disabled={!canReorderRooms}
                sx={{ mt: 1 }}
                size="medium"
                onClick={() => setReorderMode(true)}
              />
            </Box>
          </SecuredComponent>
        )}
        {isEditMode && (
          <SimpleForm
            onSubmit={handleSubmit}
            toolbar={false}
          >
            <ArrayInput
              source="rooms"
              label=" "
              defaultValue={examRooms}
            >
              <SimpleFormIterator
                sx={settings.style.iteratorWithoutGap}
                disableAdd={!addMode}
                disableReordering={!reorderMode}
                disableRemove={!deleteMode}
                disableClear
              >
                {reorderMode && <IndexField />}
                <ReferenceInput
                  source="roomId"
                  reference={administrationRequests.getRoomsSelect()}
                  // filter={{ campaignExamId: record?.campaignExamId }}
                >
                  <AutocompleteInput
                    label="Зала"
                    optionValue="id"
                    optionText={rec =>
                      `${rec.roomName} - ${rec.buildingName}${rec.capacityExam ? ` (${rec.capacityExam})` : ""}`
                    }
                    filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                    fullWidth
                  />
                </ReferenceInput>
              </SimpleFormIterator>
            </ArrayInput>
            <Box sx={{ textAlign: "left", ml: 2 }}>
              <Button
                label="Откажи"
                onClick={() => {
                  setAddMode(false);
                  setDeleteMode(false);
                  setReorderMode(false);
                }}
                variant="outlined"
                color="error"
                size="medium"
                startIcon={<CloseIcon />}
                sx={{ mr: 10 }}
              />

              <Button
                label="Запази"
                color="primary"
                variant="contained"
                size="medium"
                type="submit"
                startIcon={<SaveIcon />}
              />
            </Box>
          </SimpleForm>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ExamRoomsShow;
